import { forcedSignOut, type SignOutReason } from '@fleetspoke/core/auth';
import { actArgs, MANAGER_ACT, type SupportAct } from '@fleetspoke/core/support';
import type { Session } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { useWhoami, type Whoami } from '../data/queries';
import { setLanguage } from '../i18n';
import { supabase } from '../lib/supabase';

export type SupportEndReason = 'exit' | 'expired' | 'signed_out';

interface SessionState {
  session: Session | null;
  ready: boolean;
  me: Whoami | undefined;
  meLoading: boolean;
  meError: Error | null;
  signOut(): Promise<void>;
  /** Support mode (CR_20260913): open a customer account — as a chosen role (CR_20260924 Part C) — then work in it. */
  startSupport(companyId: string, reason: string, minutes: number, act?: SupportAct): Promise<void>;
  /** Switch the open visit to another role / reach (Part C, decision F): same visit, new claims, nothing cached. */
  setSupportRole(act: SupportAct): Promise<void>;
  extendSupport(minutes?: number): Promise<void>;
  endSupport(reason?: SupportEndReason): Promise<void>;
  /** The account whose support session just timed out (shown once on the admin side). */
  supportEnded: string | null;
  clearSupportEnded(): void;
}

const SessionContext = createContext<SessionState | null>(null);

const SIGNED_OUT_KEY = 'fsa.signed_out_reason';

/** Why the app last signed this person out on its own (the sign-in page shows it, then forgets it). */
export function signOutReason(): SignOutReason | null {
  try {
    return sessionStorage.getItem(SIGNED_OUT_KEY) as SignOutReason | null;
  } catch {
    return null;
  }
}

export function forgetSignOutReason(): void {
  try {
    sessionStorage.removeItem(SIGNED_OUT_KEY);
  } catch {
    // nothing stored
  }
}

/** app_metadata as signed into the access token by the Custom Access Token Hook. */
function tokenAppMetadata(session: Session | null): Record<string, unknown> {
  const part = session?.access_token.split('.')[1];
  if (!part) return {};
  try {
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    return (JSON.parse(json) as { app_metadata?: Record<string, unknown> }).app_metadata ?? {};
  } catch {
    return {};
  }
}

async function rpc(fn: string, args: Record<string, unknown>): Promise<void> {
  const { error } = await supabase().rpc(fn, args);
  if (error) throw Object.assign(new Error(error.message), { code: error.code });
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [ready, setReady] = useState(false);
  const [supportEnded, setSupportEnded] = useState<string | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    let active = true;
    void supabase()
      .auth.getSession()
      .then(({ data }) => {
        if (!active) return;
        setSession(data.session);
        setReady(true);
      });
    const { data } = supabase().auth.onAuthStateChange((_event, next) => {
      setSession(next);
      if (!next) queryClient.clear();
    });
    return () => {
      active = false;
      data.subscription.unsubscribe();
    };
  }, [queryClient]);

  const who = useWhoami(!!session);

  // the profile's language wins once known (the login screen uses the browser / last choice)
  useEffect(() => {
    if (who.data?.locale) setLanguage(who.data.locale);
  }, [who.data?.locale]);

  // New claims (the hook reads the support session at token issue), then nothing cached from the other side:
  // admin-mode data must never show inside a customer account, nor the reverse.
  const switchSides = useCallback(async () => {
    await supabase().auth.refreshSession();
    // who we are first: the route guards (PlatformAdminOnly, …) then leave the pages of the side we just left, so
    // their queries are not re-run with the new token — e.g. admin_company_members answering 403 to a support
    // session. One tick lets React commit that redirect before everything else is reset.
    await queryClient.resetQueries({ queryKey: ['whoami'] });
    await new Promise((resolve) => setTimeout(resolve, 0));
    await queryClient.resetQueries({ predicate: (q) => q.queryKey[0] !== 'whoami' });
  }, [queryClient]);

  const endSupport = useCallback(
    async (reason: SupportEndReason = 'exit') => {
      await rpc('end_support_session', { p_reason: reason });
      await switchSides();
    },
    [switchSides],
  );

  // A token still carrying a support claim the server no longer honours (ended elsewhere, timed out): refresh.
  const support = who.data?.support ?? null;
  const staleClaim = !!who.data && !support && !!tokenAppMetadata(session).support_session_id;
  useEffect(() => {
    if (staleClaim) void switchSides();
  }, [staleClaim, switchSides]);

  // Role changed or access removed (2026-09-25): sign out, and say why on the sign-in page.
  const forced = session ? forcedSignOut(who.data) : null;
  useEffect(() => {
    if (!forced) return;
    try {
      sessionStorage.setItem(SIGNED_OUT_KEY, forced);
    } catch {
      // storage blocked: the sign-in page just shows no reason
    }
    void supabase()
      .auth.signOut()
      .finally(() => queryClient.clear());
  }, [forced, queryClient]);

  // Time's up: close the session and go back to the admin side.
  useEffect(() => {
    if (!support) return;
    const ms = new Date(support.expires_at).getTime() - Date.now();
    const timer = setTimeout(
      () => {
        setSupportEnded(support.company_name);
        void endSupport('expired').catch(() => switchSides());
      },
      Math.max(ms, 0),
    );
    return () => clearTimeout(timer);
  }, [support, endSupport, switchSides]);

  const value: SessionState = {
    session,
    ready,
    me: session ? who.data : undefined,
    meLoading: !!session && who.isLoading,
    meError: who.error as Error | null,
    signOut: async () => {
      if (support) await rpc('end_support_session', { p_reason: 'signed_out' }).catch(() => {});
      await supabase().auth.signOut();
      queryClient.clear();
    },
    startSupport: async (companyId, reason, minutes, act = MANAGER_ACT) => {
      setSupportEnded(null);
      await rpc('start_support_session', {
        p_company: companyId,
        p_reason: reason,
        p_minutes: minutes,
        p_origin: 'web',
        ...actArgs(act),
      });
      await switchSides();
    },
    setSupportRole: async (act) => {
      await rpc('set_support_role', actArgs(act));
      await switchSides();
    },
    extendSupport: async (minutes = 60) => {
      await rpc('extend_support_session', { p_minutes: minutes });
      await queryClient.invalidateQueries({ queryKey: ['whoami'] });
    },
    endSupport,
    supportEnded,
    clearSupportEnded: () => setSupportEnded(null),
  };
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession outside SessionProvider');
  return ctx;
}

/**
 * Managers of the account may manage its settings — a super admin inside one only while the visit acts as manager
 * (whoami reports the visit's role; CR_20260924 Part C). Trusting `platform_admin` here would give a visit acting as
 * warehouse staff the manager screens.
 */
export function canManage(me: Whoami | undefined): boolean {
  return !!me?.company && me.role === 'manager';
}

/** Warehouses in the menu (CR_Manager_vs_User_role P1): managers and warehouse staff — never a project user. */
export function seesWarehouses(me: Whoami | undefined): boolean {
  return canManage(me) || me?.role === 'warehouse_staff' || me?.role === 'user';
}

/** Projects in the menu: managers, project users, and warehouse staff who were given projects. */
export function seesProjects(me: Whoami | undefined): boolean {
  if (canManage(me) || me?.role === 'project_user' || me?.role === 'user') return true;
  return me?.role === 'warehouse_staff' && !!me.project_scope && me.project_scope !== 'none';
}

/**
 * A super admin outside any customer account: lives in /admin only (CR_20260913 part C). Customer screens need a
 * company — without one the dashboard is empty and lists would mix every customer through the RLS carve-out.
 */
export function isAdminHome(me: Whoami | undefined): boolean {
  return !!me?.platform_admin && !me.company;
}

/** A super admin working inside a customer account (support mode): manager powers there, no admin area. */
export function isSupporting(me: Whoami | undefined): boolean {
  return !!me?.support;
}
